// Aggregation behind the "רכש לפי ספקים" donut (SupplierDonutCard). Kept in its
// own recharts-free module so the edge cases — ranking, the top-5 / remainder
// split, and the share math — are unit-tested without dragging the chart
// library through jest.

// Slice colours, fixed by rank per the design spec. Index 0 = biggest supplier.
export const SLICE_COLORS = ['#4A5ABF', '#AA7FFA', '#64C3D3', '#FBDE7F', '#FA8570'];
export const REMAINDER_COLOR = '#D9D9D9';
export const TOP_N = 5;

// Which figure the donut measures. Both are always carried on every slice for
// the tooltip; `basis` only decides which one draws the arcs and the shares.
export const BASIS_NET = 'net';
export const BASIS_GROSS = 'gross';
export const basisLabel = (basis) =>
  (basis === BASIS_GROSS ? 'סכום (כולל מע״מ)' : 'סכום (ללא מע״מ)');

// Just the VAT qualifier, for places that already say what the number is (the
// totals strip says "סה״כ רכש", so repeating "סכום" there says it twice).
export const basisTag = (basis) =>
  (basis === BASIS_GROSS ? 'כולל מע״מ' : 'ללא מע״מ');

const metricOf = (s, basis) =>
  (basis === BASIS_GROSS ? (s.total_amount_gross || 0) : (s.total_amount || 0));

// Rank suppliers by spend and shape them into donut slices.
//
// `basis` is a real re-computation, not a relabel: gross is summed PER SUPPLIER
// by the server rather than derived from the net total, because in the admin
// cross-restaurant view rows can span restaurants on different VAT rates. So
// switching the basis can genuinely re-rank the suppliers, and multiplying the
// net total by one rate would be wrong.
//
// Only positive spenders count: a zero or credit-negative supplier has no slice,
// and a negative arc would render as a wedge that lies about its size — so the
// grand total (the % denominator) is the sum of the positive spenders only.
// With more than `topN` suppliers the tail collapses into one "שאר הספקים"
// bucket; with `topN` or fewer, every supplier is drawn individually.
export function buildSupplierSlices(suppliers, { topN = TOP_N, basis = BASIS_NET } = {}) {
  const ranked = (suppliers || [])
    .filter(s => metricOf(s, basis) > 0)
    .sort((a, b) => metricOf(b, basis) - metricOf(a, basis));
  const grandTotal = ranked.reduce((sum, s) => sum + metricOf(s, basis), 0);

  const showRemainder = ranked.length > topN;
  const tops = showRemainder ? ranked.slice(0, topN) : ranked;
  const rest = showRemainder ? ranked.slice(topN) : [];

  const slices = tops.map((s, i) => ({
    name: s.name,
    // `value` is what recharts draws; net/gross stay beside it so the tooltip
    // can always report BOTH regardless of which one is driving the chart.
    value: metricOf(s, basis),
    net: s.total_amount || 0,
    gross: s.total_amount_gross || 0,
    color: SLICE_COLORS[i],
    pct: grandTotal ? (metricOf(s, basis) / grandTotal) * 100 : 0,
  }));
  if (showRemainder) {
    const restValue = rest.reduce((a, s) => a + metricOf(s, basis), 0);
    slices.push({
      name: 'שאר הספקים',
      value: restValue,
      net: rest.reduce((a, s) => a + (s.total_amount || 0), 0),
      gross: rest.reduce((a, s) => a + (s.total_amount_gross || 0), 0),
      color: REMAINDER_COLOR,
      pct: grandTotal ? (restValue / grandTotal) * 100 : 0,
      isRemainder: true,
      count: rest.length,
    });
  }

  const topValue = tops.reduce((a, s) => a + metricOf(s, basis), 0);
  return {
    slices,
    topCount: tops.length,
    topShare: grandTotal ? (topValue / grandTotal) * 100 : 0,
    // The period's whole spend on the chosen basis — what the summary strip
    // reports. Derived here so the strip and the arcs can never disagree.
    total: grandTotal,
    hasData: ranked.length > 0,
  };
}

// One decimal, but drop a trailing ".0" so whole percentages read clean.
export function pctLabel(n) {
  const r = Math.round(n * 10) / 10;
  return `${Number.isInteger(r) ? r : r.toFixed(1)}%`;
}
